import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader } from "@shared/ui/card";
import { Tabs } from "@shared/ui/tabs";
import {
  ArrowLeft,
  Phone,
  Mail,
  MapPin,
  Home,
  CalendarIcon,
  Wrench,
  HardHat,
  FileSpreadsheet,
  ClipboardList,
  Receipt,
  Activity,
  BadgeCheck,
  Hammer,
  Timer,
  AlertCircle,
} from "lucide-react";

// Import tab components
import { AssignmentsTab } from "./tabs/assignments";
import { TransactionsTab } from "./tabs/transactions";
import { ActivityTab } from "./tabs/activityLog";
import { CertificationsTab } from "./tabs/certifications";
import { SkillsTab } from "./tabs/skills";
import { AvailabilityTab } from "./tabs/availability";
import { useGetFieldForceDetails } from "../../hooks";
import { Badge } from "@shared/ui/badge";
import { usePageViewTimer } from "@shared/hooks/usePageViewTimer";

const FieldForceView = () => {
  const { id, ff_no } = useParams<{ id: string; ff_no: string }>();
  const navigate = useNavigate();
  usePageViewTimer({pageName: `Field Force - ${ff_no}`})
  const { data: technician } = useGetFieldForceDetails(ff_no);
  const handleBackToList = () => {
    navigate("/service-orders/field-force/");
  };

  const getRoleIcon = (roleIcon: string) => {
    switch (roleIcon) {
      case "admin":
        return <Wrench size={14} className="text-blue-500" />;
      case "technician":
        return <HardHat size={14} className="text-blue-500" />;
      default:
        return <Wrench size={14} className="text-blue-500" />;
    }
  };

  // Tab configuration similar to SettingsLanding
  const tabComponents = {
    assignments: {
      label: "Assignments",
      shortLabel: "Assignments",
      icon: <ClipboardList size={16} />,
      component: <AssignmentsTab technicianId={id} />,
    },
    transactions: {
      label: "Transactions",
      shortLabel: "Transactions",
      icon: <Receipt size={16} />,
      component: <TransactionsTab />,
    },
    activity: {
      label: "Activity Log",
      shortLabel: "Activity",
      icon: <Activity size={16} />,
      component: <ActivityTab />,
    },
    certifications: {
      label: "Certifications",
      shortLabel: "Certifications",
      icon: <BadgeCheck size={16} />,
      component: <CertificationsTab technicianId={id} />,
    },
    skills: {
      label: "Skills",
      shortLabel: "Skills",
      icon: <Hammer size={16} />,
      component: <SkillsTab technicianId={id} />,
    },
    availability: {
      label: "Availability",
      shortLabel: "Availability",
      icon: <Timer size={16} />,
      component: <AvailabilityTab technicianId={id} />,
    },
  };

  const urlMapping = {
    assignments: "assignments",
    transactions: "transactions",
    activity: "activity",
    certifications: "certifications",
    skills: "skills",
    availability: "availability",
  };

  return (
    <div className="container mx-auto p-6 animate-fade-in space-y-6">
      {/* Back button and header */}
      <div className="flex items-center justify-between mb-6">
        <Button
          variant="ghost"
          onClick={handleBackToList}
          className="flex items-center"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Field Force List
        </Button>
      </div>

      {/* Technician profile header */}
      <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
        <div className="p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-3xl font-medium shadow-sm">
                {technician?.name?.charAt(0)}
              </div>
              <div>
                <h1 className="card-value-text flex items-center gap-2">
                  {technician?.name}
                  <Badge
                    variant="outline"
                    className={`ml-2 ${
                      technician?.isActive
                        ? "bg-green-100 text-green-800 border border-green-300"
                        : "bg-red-100 text-red-800 border border-red-300"
                    }`}
                  >
                    {technician?.isActive ? "Active" : "Inactive"}
                  </Badge>
                </h1>
                <p className="card-subheading-text flex items-center gap-1 mt-1">
                  {getRoleIcon(technician?.roleIcon)}
                  {technician?.roleName} at SMART360 Utilities
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick stats cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 border-b">
          <div className="p-6 border-r text-center">
            <p className="card-subheading-text mb-1">Field Force ID</p>
            <p className="card-value-text">{technician?.fieldForceNo}</p>
          </div>
          <div className="p-6 border-r text-center">
            <p className="card-subheading-text mb-1">Assigned SOs</p>
            <p className="card-value-text ">{technician?.total}</p>
          </div>
          <div className="p-6 border-r text-center">
            <p className="card-subheading-text mb-1">Completed SOs</p>
            <p className="card-value-text ">{technician?.completed}</p>
          </div>
          <div className="p-6 text-center">
            <p className="card-subheading-text mb-1">Joining Date</p>
            <p className="card-value-text">{technician?.createdDate}</p>
          </div>
        </div>
      </div>

      {/* Main content with tabs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Left Sidebar with personal info */}
        <div className="md:col-span-1">
          <Card className="p-0">
            <CardHeader className="flex flex-col space-y-1.5 p-4 pb-0">
              <h3 className="card-heading-text">Personal Information</h3>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                <div className="p-4">
                  <p className="text-xs uppercase text-slate-500 font-medium mb-1.5">
                    Contact
                  </p>
                  <p className="font-medium text-slate-900 flex items-center gap-1">
                    <Phone size={14} className="text-blue-500" />
                    {technician?.contactNumber || "N/A"}
                  </p>
                </div>

                <div className="p-4">
                  <p className="text-xs uppercase text-slate-500 font-medium mb-1.5">
                    Email
                  </p>
                  <p className="font-medium text-slate-900 flex items-center gap-1">
                    <Mail size={14} className="text-blue-500" />
                    {technician?.email || "N/A"}
                  </p>
                </div>
                <div className="p-4">
                  <p className="text-xs uppercase text-slate-500 font-medium mb-1.5">
                    Area
                  </p>
                  <p className="font-medium text-slate-900">
                    {technician?.areaName.map((area: string) => area).join(", ") || "N/A"}
                  </p>
                </div>
                <div className="p-4">
                  <p className="text-xs uppercase text-slate-500 font-medium mb-1.5">
                    Sub Area
                  </p>
                  <p className="font-medium text-slate-900">
                    {technician?.subAreaName.map((subArea: string) => subArea).join(", ") || "N/A"}
                  </p>
                </div>

                {technician?.addressMap?.addressLine && (
                  <div className="p-4">
                    <p className="text-xs uppercase text-slate-500 font-medium mb-1.5">
                      Address
                    </p>
                    <p className="text-sm text-slate-900 flex items-start gap-1">
                      <Home size={14} className="text-blue-500 mt-1" />
                      {technician?.addressMap?.addressLine || "N/A"}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content with Tabs - 3/4 width */}
        <div className="md:col-span-3">
          <Card>
            <Tabs
              defaultValue="assignments"
              urlMapping={urlMapping}
              tabComponents={tabComponents}
              tabsListClassName="w-full justify-start px-4 h-14 bg-slate-50 border-b rounded-t-lg rounded-b-none grid grid-cols-6"
              idPrefix="technician-tab"
              level={1} // Second-level tabs
            />
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FieldForceView;
